'use client'

import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Expense, ExpenseCategory, ExpenseListItem, ExpenseSyncResult } from '@garage/shared'
import { EXPENSE_CATEGORIES, EXPENSE_CATEGORY_LABELS } from '@garage/shared'
import {
  PageHeader,
  Button,
  IconButton,
  Segmented,
  Checkbox,
  DataTable,
  TextField,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Skeleton,
  useSnackbar,
  type Column,
  EditIcon,
  DeleteIcon,
  AddIcon,
  ChevronRightIcon,
  SyncIcon,
} from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useDebounced, useFormatters } from '@/lib/hooks.js'
import { formatVatDate } from '@/lib/vat-date.js'
import { ExpenseFormDialog } from '@/components/ExpenseFormDialog.js'
import { ExpenseDetailDialog } from '@/components/ExpenseDetailDialog.js'
import type { ExpenseDetail } from '@garage/shared'

/** The calendar quarter ("YYYY-MM-DD" bounds, inclusive) a given quarter number falls in. */
function quarterRange(year: number, quarter: number): { from: string; to: string } {
  const startMonth = (quarter - 1) * 3 + 1
  const endMonth = startMonth + 2
  const lastDay = new Date(year, endMonth, 0).getDate()
  return {
    from: `${year}-${String(startMonth).padStart(2, '0')}-01`,
    to: `${year}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
  }
}

function currentQuarter(): { year: number; quarter: number } {
  const now = new Date()
  return { year: now.getFullYear(), quarter: Math.floor(now.getMonth() / 3) + 1 }
}

function monthLabel(month: string, language: string): string {
  const [y, m] = month.split('-').map(Number)
  const locale = language === 'ar' ? 'ar-u-nu-latn' : 'en-US'
  return new Date(y!, m! - 1, 1).toLocaleDateString(locale, { month: 'short', year: 'numeric' })
}

/** First and last day ("YYYY-MM-DD") of a given month, 0-indexed like Date. */
function monthRange(year: number, month: number): { from: string; to: string } {
  const lastDay = new Date(year, month + 1, 0).getDate()
  return {
    from: `${year}-${String(month + 1).padStart(2, '0')}-01`,
    to: `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
  }
}

const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
] as const

/**
 * Supplier purchase invoices for the quarterly VAT return: general expenses,
 * parts and paint, each dated, with the before/VAT/total split the return
 * needs and the supplier's TRN (or lack of one — a non-VAT-registered
 * supplier's invoice carries no VAT at all).
 *
 * @param embedded - When true, doesn't render its own PageHeader
 */
export function AccountingPage({ embedded = false }: { embedded?: boolean } = {}) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const formatters = useFormatters()
  const { t, i18n } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const monthName = (month: number) => t(`calendar.months.${MONTH_KEYS[month]}`)
  const categoryLabel = (c: ExpenseCategory) => t(`accounting.categories.${c}`, EXPENSE_CATEGORY_LABELS[c])

  const CATEGORY_TABS: { value: ExpenseCategory | 'all'; label: string }[] = [
    { value: 'all', label: t('status.all') },
    ...EXPENSE_CATEGORIES.map((c) => ({ value: c, label: categoryLabel(c) })),
  ]

  const categoryFilter = (searchParams.get('category') ?? 'all') as ExpenseCategory | 'all'
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounced(searchInput, 250)

  const defaultQuarter = currentQuarter()
  const [year, setYear] = useState(defaultQuarter.year)
  const [quarter, setQuarter] = useState(defaultQuarter.quarter)

  // One pair of boxes drives the VAT return and the expense table alike.
  // Both ticked is the normal view, so the filter is omitted from the requests.
  const [withTrn, setWithTrn] = useState(true)
  const [withoutTrn, setWithoutTrn] = useState(true)
  const trn: 'with' | 'without' | 'none' | undefined =
    withTrn && withoutTrn ? undefined : withTrn ? 'with' : withoutTrn ? 'without' : 'none'

  const today = new Date()
  const [listYear, setListYear] = useState(today.getFullYear())
  const [listMonth, setListMonth] = useState(today.getMonth())

  const [deleteExpenseId, setDeleteExpenseId] = useState<string | null>(null)
  const [detailExpenseId, setDetailExpenseId] = useState<string | null>(null)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [editingExpense, setEditingExpense] = useState<ExpenseDetail | Expense | undefined>()
  const [formDialogOpen, setFormDialogOpen] = useState(false)
  const [syncResult, setSyncResult] = useState<ExpenseSyncResult | null>(null)

  const listRange = monthRange(listYear, listMonth)

  const { data, isLoading, isError } = useQuery({
    queryKey: qk.expenses({
      category: categoryFilter === 'all' ? undefined : categoryFilter,
      q: debouncedSearch,
      trn,
      ...listRange,
    }),
    queryFn: () =>
      api.expenses.list({
        category: categoryFilter === 'all' ? undefined : categoryFilter,
        q: debouncedSearch || undefined,
        trn,
        from: listRange.from,
        to: listRange.to,
        pageSize: 200,
      }),
  })

  const goToListMonth = (delta: number) => {
    const next = new Date(listYear, listMonth + delta, 1)
    setListYear(next.getFullYear())
    setListMonth(next.getMonth())
  }

  const { from, to } = quarterRange(year, quarter)
  const { data: vatSummary, isLoading: isSummaryLoading } = useQuery({
    queryKey: qk.expensesVatSummary({ from, to, trn }),
    queryFn: () => api.expenses.vatSummary({ from, to, trn }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.expenses.remove(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: qk.expenses() })
      queryClient.invalidateQueries({ queryKey: qk.expense(id) })
      queryClient.invalidateQueries({ queryKey: ['expenses-vat-summary'] })
      snackbar.success(t('accounting.expenseRemoved'))
      setDeleteExpenseId(null)
    },
    onError: (error) => {
      snackbar.error(error instanceof ApiError ? error.message : t('accounting.failedToRemoveExpense'))
    },
  })

  const syncMutation = useMutation({
    mutationFn: (category: ExpenseCategory) => api.expenses.sync(category, listRange),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: qk.expenses() })
      queryClient.invalidateQueries({ queryKey: ['expenses-vat-summary'] })
      setSyncResult(result)
    },
    onError: (error) => {
      snackbar.error(error instanceof ApiError ? error.message : t('accounting.syncFailed'))
    },
  })

  const handleSync = () => {
    if (categoryFilter === 'all') {
      snackbar.error(t('accounting.chooseCategoryFirst'))
      return
    }
    syncMutation.mutate(categoryFilter)
  }

  const handleCategoryChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams)
    if (value === 'all') newParams.delete('category')
    else newParams.set('category', value)
    setSearchParams(newParams)
  }

  const handleShowDetail = (expense: ExpenseListItem) => {
    setDetailExpenseId(expense.id)
    setDetailDialogOpen(true)
  }

  const handleDetailClose = () => {
    setDetailDialogOpen(false)
    setDetailExpenseId(null)
  }

  const handleEditFromDetail = (expense: ExpenseDetail) => {
    setEditingExpense(expense)
    setDetailDialogOpen(false)
    setDetailExpenseId(null)
    setFormDialogOpen(true)
  }

  const handleEdit = (expense: ExpenseListItem | ExpenseDetail) => {
    setEditingExpense(expense)
    setFormDialogOpen(true)
  }

  const handleFormClose = () => {
    setFormDialogOpen(false)
    setEditingExpense(undefined)
  }

  const handleAddNew = () => {
    setEditingExpense(undefined)
    setFormDialogOpen(true)
  }

  const items = data?.items ?? []
  const hasSearchQuery = debouncedSearch.trim() !== ''
  const isEmpty = items.length === 0

  // Totals of the rows on screen, so they follow the TRN filter and search.
  const shownTotals = items.reduce(
    (sum, row) => ({
      beforeVatMinor: sum.beforeVatMinor + row.amountBeforeVatMinor,
      vatMinor: sum.vatMinor + row.vatMinor,
    }),
    { beforeVatMinor: 0, vatMinor: 0 },
  )

  // Shown beside the quarter total and above the table whenever a box is unticked,
  // so a filtered figure is never mistaken for total spend.
  const trnFilterNote = trn === undefined ? null : (
    <span className="text-body-sm text-warning">
      {trn === 'with' && t('accounting.showingWithTrnOnly')}
      {trn === 'without' && t('accounting.showingWithoutTrnOnly')}
      {trn === 'none' && t('accounting.showingNothing')}
    </span>
  )

  const columns: Column<ExpenseListItem>[] = [
    {
      key: 'date',
      wrap: true,
      header: t('calendar.date'),
      cell: (row) => <div>{formatVatDate(row.date)}</div>,
    },
    {
      key: 'invoiceNumber',
      wrap: true,
      header: t('accounting.invoiceNumber'),
      cell: (row) => <div className="ref">{row.invoiceNumber}</div>,
    },
    {
      key: 'supplierName',
      wrap: true,
      header: t('accounting.supplier'),
      cell: (row) => (
        <div>
          {row.supplierName}
          {row.jobcardNumber && (
            <span className="text-on-surface-variant"> · {t('accounting.for')} <span className="ref">{row.jobcardNumber}</span></span>
          )}
        </div>
      ),
    },
    {
      key: 'supplierTrn',
      wrap: true,
      header: t('accounting.trn'),
      cell: (row) =>
        row.supplierTrn ? (
          <div className="ref">{row.supplierTrn}</div>
        ) : (
          <span className="text-on-surface-variant">{t('accounting.noTrn')}</span>
        ),
      hideOnMobile: true,
    },
    {
      key: 'amountBeforeVatMinor',
      header: t('accounting.beforeVat'),
      cell: (row) => <div className="tabular">{formatters.amount(row.amountBeforeVatMinor)}</div>,
      numeric: true,
    },
    {
      key: 'vatMinor',
      header: t('accounting.vat'),
      cell: (row) => <div className="tabular">{formatters.amount(row.vatMinor)}</div>,
      numeric: true,
    },
    {
      key: 'totalMinor',
      header: t('dashboard.columns.total'),
      cell: (row) => (
        <div className="tabular font-medium">
          {formatters.amount(row.amountBeforeVatMinor + row.vatMinor)}
        </div>
      ),
      numeric: true,
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex gap-2 justify-end">
          <IconButton
            icon={<EditIcon className="w-5 h-5" />}
            label={t('accounting.editExpense')}
            variant="standard"
            onClick={(e) => {
              e.stopPropagation()
              handleEdit(row)
            }}
          />
          <IconButton
            icon={<DeleteIcon className="w-5 h-5" />}
            label={t('accounting.removeExpense')}
            variant="danger"
            onClick={(e) => {
              e.stopPropagation()
              setDeleteExpenseId(row.id)
            }}
          />
        </div>
      ),
    },
  ]

  const yearOptions = Array.from({ length: 5 }, (_, i) => {
    const y = defaultQuarter.year - 3 + i
    return { value: String(y), label: String(y) }
  })
  const quarterOptions = [1, 2, 3, 4].map((q) => ({ value: String(q), label: `Q${q}` }))

  return (
    <div className="space-y-8">
      {!embedded && (
        <PageHeader
          title={t('accounting.title')}
          subtitle={t('accounting.subtitle')}
          actions={
            <Button variant="filled" icon={<AddIcon className="w-5 h-5" />} onClick={handleAddNew}>
              {t('accounting.addExpense')}
            </Button>
          }
        />
      )}

      {embedded && (
        <div className="flex justify-end">
          <Button variant="filled" icon={<AddIcon className="w-5 h-5" />} onClick={handleAddNew}>
            {t('accounting.addExpense')}
          </Button>
        </div>
      )}

      {/* VAT return summary: pick a quarter, see the monthly and combined figures to submit. */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <h2 className="text-title-md text-on-surface flex-1">{t('accounting.vatReturn')}</h2>
          <div className="flex flex-wrap items-center gap-4 pb-1">
            <Checkbox checked={withTrn} onChange={setWithTrn} label={t('accounting.withTrn')} />
            <Checkbox checked={withoutTrn} onChange={setWithoutTrn} label={t('accounting.withoutTrn')} />
          </div>
          <Segmented
            label={t('accounting.year')}
            options={yearOptions}
            value={String(year)}
            onChange={(v) => setYear(Number(v))}
          />
          <Segmented
            label={t('accounting.quarter')}
            options={quarterOptions}
            value={String(quarter)}
            onChange={(v) => setQuarter(Number(v))}
          />
        </div>

        {isSummaryLoading || !vatSummary ? (
          <Skeleton lines={4} />
        ) : (
          <div className="bg-surface-container border border-outline-variant rounded-lg overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-outline-variant">
              {vatSummary.months.map((m) => (
                <div key={m.month} className="p-5 space-y-2">
                  <div className="text-label-md text-on-surface-variant">{monthLabel(m.month, i18n.language)}</div>
                  <div className="space-y-1 text-body-sm">
                    {EXPENSE_CATEGORIES.map((c) => (
                      <div key={c} className="flex items-baseline justify-between text-on-surface-variant">
                        <span>{categoryLabel(c)}</span>
                        <span className="tabular">{formatters.amount(m.categories[c].beforeVatMinor)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="pt-2 border-t border-outline-variant space-y-1">
                    <div className="flex items-baseline justify-between text-body-sm text-on-surface-variant">
                      <span>{t('accounting.beforeVat')}</span>
                      <span className="tabular">{formatters.amount(m.beforeVatMinor)}</span>
                    </div>
                    <div className="flex items-baseline justify-between text-body-sm text-on-surface-variant">
                      <span>{t('accounting.vat')}</span>
                      <span className="tabular">{formatters.amount(m.vatMinor)}</span>
                    </div>
                    <div className="flex items-baseline justify-between text-title-md text-on-surface">
                      <span>{t('dashboard.columns.total')}</span>
                      <span className="tabular">{formatters.money(m.totalMinor)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* The number that actually gets submitted on the return. */}
            <div className="flex flex-wrap items-baseline justify-between gap-4 px-5 py-4 bg-surface-low border-t border-outline-variant">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-label-lg text-on-surface">{t('accounting.quarterTotal', { quarter, year })}</span>
                {trnFilterNote}
              </div>
              <div className="flex gap-6 text-body-md text-on-surface-variant">
                <span>{t('accounting.beforeVat')} <span className="tabular text-on-surface">{formatters.money(vatSummary.beforeVatMinor)}</span></span>
                <span>{t('accounting.vat')} <span className="tabular text-on-surface">{formatters.money(vatSummary.vatMinor)}</span></span>
                <span className="text-title-md text-on-surface">{t('dashboard.columns.total')} <span className="tabular">{formatters.money(vatSummary.totalMinor)}</span></span>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Expense entries: the data the summary above is built from, one
          month at a time rather than every invoice ever entered in one
          long table. */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IconButton
              icon={<ChevronRightIcon className="w-5 h-5 rotate-180 rtl:rotate-0" />}
              label={t('calendar.previousMonth')}
              variant="standard"
              onClick={() => goToListMonth(-1)}
            />
            <h2 className="text-title-md text-on-surface w-40 text-center">
              {monthName(listMonth)} {listYear}
            </h2>
            <IconButton
              icon={<ChevronRightIcon className="w-5 h-5 rtl:rotate-180" />}
              label={t('calendar.nextMonth')}
              variant="standard"
              onClick={() => goToListMonth(1)}
            />
          </div>
          <Button
            variant="text"
            onClick={() => { setListYear(today.getFullYear()); setListMonth(today.getMonth()) }}
          >
            {t('calendar.today')}
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center sm:justify-between">
          <Segmented
            options={CATEGORY_TABS}
            value={categoryFilter}
            onChange={handleCategoryChange}
            label={t('accounting.category')}
          />
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:items-center">
            <Button
              variant="outlined"
              icon={<SyncIcon className="w-5 h-5" />}
              loading={syncMutation.isPending}
              onClick={handleSync}
              title={t('accounting.syncOnlyImports', { month: monthName(listMonth), year: listYear })}
            >
              {t('accounting.syncFromExcel')}
            </Button>
            <div className="w-full sm:max-w-xs">
              <TextField
                label={t('common.search')}
                value={searchInput}
                onChange={setSearchInput}
                placeholder={t('accounting.searchPlaceholder')}
                fullWidth
              />
            </div>
          </div>
        </div>

        {trnFilterNote && <div>{trnFilterNote}</div>}

        {isLoading ? (
          <Skeleton lines={3} />
        ) : isError ? (
          <EmptyState title={t('accounting.errorLoadingExpenses')} description={t('clients.tryRefreshing')} />
        ) : isEmpty ? (
          hasSearchQuery ? (
            <EmptyState title={t('accounting.noExpensesFound')} description={t('staff.adjustSearch')} />
          ) : (
            <EmptyState
              title={t('accounting.noExpensesInMonth', { month: monthName(listMonth), year: listYear })}
              description={t('accounting.addInvoiceOrCheckMonth')}
              action={<Button variant="tonal" onClick={handleAddNew}>{t('accounting.addExpense')}</Button>}
            />
          )
        ) : (
          <div className="bg-surface-container border border-outline-variant rounded-lg overflow-hidden">
            <DataTable<ExpenseListItem>
              columns={columns}
              rows={items}
              rowKey={(e) => e.id}
              onRowClick={(expense) => handleShowDetail(expense)}
              stackOnMobile
            />
            <div className="flex flex-wrap items-baseline justify-between gap-4 px-5 py-3 bg-surface-low border-t border-outline-variant">
              <span className="text-label-lg text-on-surface">{t('accounting.tableTotal')}</span>
              <div className="flex gap-6 text-body-md text-on-surface-variant">
                <span>{t('accounting.beforeVat')} <span className="tabular text-on-surface">{formatters.amount(shownTotals.beforeVatMinor)}</span></span>
                <span>{t('accounting.vat')} <span className="tabular text-on-surface">{formatters.amount(shownTotals.vatMinor)}</span></span>
                <span className="text-title-md text-on-surface">{t('dashboard.columns.total')} <span className="tabular">{formatters.amount(shownTotals.beforeVatMinor + shownTotals.vatMinor)}</span></span>
              </div>
            </div>
          </div>
        )}
      </section>

      <ExpenseDetailDialog
        open={detailDialogOpen}
        expenseId={detailExpenseId}
        onClose={handleDetailClose}
        onEdit={handleEditFromDetail}
      />

      <ExpenseFormDialog open={formDialogOpen} expense={editingExpense} onClose={handleFormClose} />

      <Dialog
        open={!!syncResult}
        onClose={() => setSyncResult(null)}
        title={t('accounting.syncFromExcel')}
        size="md"
        actions={
          <Button variant="filled" onClick={() => setSyncResult(null)}>
            {t('accounting.done')}
          </Button>
        }
      >
        {syncResult && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-body-md">
              <span><span className="text-title-md text-on-surface">{syncResult.added}</span> {t('accounting.added')}</span>
              <span><span className="text-title-md text-on-surface">{syncResult.skipped}</span> {t('accounting.alreadyThere')}</span>
              {syncResult.outsideRange > 0 && (
                <span><span className="text-title-md text-on-surface">{syncResult.outsideRange}</span> {t('accounting.outsideMonth', { month: monthName(listMonth), year: listYear })}</span>
              )}
              {syncResult.errors.length > 0 && (
                <span><span className="text-title-md text-error">{syncResult.errors.length}</span> {t('accounting.skippedWithErrors')}</span>
              )}
            </div>
            {syncResult.errors.length > 0 && (
              <div className="max-h-64 overflow-y-auto space-y-1 border border-outline-variant rounded-md p-3 bg-surface-high">
                {syncResult.errors.map((e, i) => (
                  <div key={i} className="text-body-sm text-on-surface-variant">
                    {t('accounting.row', { row: e.row })}: {e.message}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        open={!!deleteExpenseId}
        title={t('accounting.removeExpenseTitle')}
        message={t('accounting.removeExpenseMessage')}
        confirmLabel={t('common.remove')}
        cancelLabel={t('common.cancel')}
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteExpenseId) deleteMutation.mutate(deleteExpenseId)
        }}
        onCancel={() => setDeleteExpenseId(null)}
      />
    </div>
  )
}
